import { resetUserMfaMutation, userMfaStatusForAdminQueryAtom } from "@/admin/users/usersQuery.ts";
import type { GQUserItem } from "@/admin/users/UsersTable.tsx";
import type { BadgeProps } from "@/components/Badge.tsx";
import { Badge } from "@/components/Badge.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { SkeletonFixed } from "@/components/loading/SkeletonFixed.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { useQueryResultAtom } from "@/utils/query/useSuspenseQueryAtom.ts";

interface MfaModalModalProps {
    item: Pick<GQUserItem, "id" | "name">;
}

export function MfaModalContent({ item }: MfaModalModalProps) {
    const user_id = Number(item.id);
    const { name } = item;

    const { data, isPending } = useQueryResultAtom(userMfaStatusForAdminQueryAtom(user_id));
    const resetMutation = useStandardMutation(resetUserMfaMutation);

    async function resetMfa() {
        await resetMutation.mutateAsync({ user_id });
    }

    const badgeInfo: { cls: BadgeProps["color"]; text: string } = {
        cls: data?.success ? "success" : "error",
        text: data?.success ? "Enabled" : "Disabled",
    };

    return (
        <WModalContent
            title="Manage user's MFA"
            label="Reset MFA"
            autoFocus
            isActionDisabled={!data?.success}
            modalAction={resetMfa}
        >
            <p className="pb-2">{`Here you can manage MFA for "${name}".`}</p>
            {!isPending && (
                <>
                    <Badge noDot color={badgeInfo.cls}>
                        {badgeInfo.text}
                    </Badge>{" "}
                </>
            )}
            <div className="pb-4 pt-2">
                {isPending ? (
                    <SkeletonFixed ems={55} lines={3} words={4} />
                ) : data?.success ? (
                    "Multi-factor authentication (MFA) is active for this user. You can reset it, which deactivates it and allows the user to start the enrollment process again."
                ) : (
                    "Multi-factor authentication (MFA) is disabled or unavailable (if they use social login methods) for this user. No actions available at the moment."
                )}
            </div>
        </WModalContent>
    );
}

export function MfaBlock(props: MfaModalModalProps) {
    return (
        <WModal button={<WModalButton inTable label="Manage MFA" size="xs" icon="i-lucide:user-lock" />}>
            <MfaModalContent {...props} />
        </WModal>
    );
}
